import React, { useState } from "react"
import { useTrackView } from "#/utils/heap"
import { FormattedMessage, useIntl } from "react-intl"
import { RegistrationFlowLayout } from "./RegistrationLayouts"
import { RegistrationFlowTitle } from "./RegistrationFlowTitle"
import { RegistrationButtonFooter, ContinueButton } from "./RegistrationButtons"
import { Form, FormTextField, useWatch } from "@carrotfertility/carotene-core-x"
import { Box, Link, Typography } from "@carrotfertility/carotene-core"
import { EmployeeSupportUrl } from "../../../utils/EmployeeSupportLink"

interface ConfirmAccountEmailProps {
  email: string
  onBack: () => void
  onComplete: () => void
  employeeId: number
}

interface ConfirmEmailInputProps extends ConfirmAccountEmailProps {
  defaultEmail: string
  onAccountNotFound: (email: string) => void
}

function ConfirmEmailContinueButton(): JSX.Element {
  const emailValue = useWatch({ name: "email" })

  return (
    <ContinueButton disabled={!emailValue}>
      <FormattedMessage defaultMessage="Continue" />
    </ContinueButton>
  )
}

function ConfirmEmailInput({
  email,
  defaultEmail,
  onBack,
  onAccountNotFound,
  onComplete,
  employeeId
}: ConfirmEmailInputProps): JSX.Element {
  useTrackView("ConfirmEmailRegFlow", "Reg flow confirm email screen")
  const intl = useIntl()

  const header = intl.formatMessage({ defaultMessage: "Please confirm your email" })

  const handleSubmit = (formValues: { email: string }): void => {
    // Validate entered email
    if (formValues.email.toLowerCase() !== email.toLowerCase()) {
      onAccountNotFound(formValues.email)
    } else {
      onComplete()
    }
  }

  return (
    <>
      <RegistrationFlowTitle title={header} />
      <RegistrationFlowLayout
        header={header}
        description={
          <Typography>
            <FormattedMessage defaultMessage="Enter your company email address or the address your benefit sponsor uses for you." />
          </Typography>
        }
        interactiveElements={
          <Form onSubmit={handleSubmit} mode="onSubmit" defaultValues={{ email: defaultEmail }}>
            <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
              <FormTextField
                name="email"
                type="email"
                label={intl.formatMessage({ defaultMessage: "Email" })}
                registerOptions={{
                  required: true
                }}
              />
              <RegistrationButtonFooter onBack={onBack} continueButton={<ConfirmEmailContinueButton />} />
            </Box>
          </Form>
        }
        employeeId={employeeId}
      />
    </>
  )
}

function AccountNotFound({ onBack, employeeId }: { onBack: () => void; employeeId: number }): JSX.Element {
  useTrackView("AccountNotFoundRegFlow", "Reg flow account not found screen")
  const intl = useIntl()
  const header = intl.formatMessage({ defaultMessage: "Account not found" })

  return (
    <>
      <RegistrationFlowTitle title={header} />
      <RegistrationFlowLayout
        header={header}
        description={
          <Typography>
            <FormattedMessage
              defaultMessage="Sorry, we couldn't find your account with the information provided. Please make sure you are entering your company email address or the address your benefit sponsor uses for you, not a personal email address. If you continue having trouble, <link>contact our Care Team</link> for help."
              values={{
                link: (text) => (
                  <Link color="inherit" href={EmployeeSupportUrl} target="_blank">
                    {text}
                  </Link>
                )
              }}
            />
          </Typography>
        }
        interactiveElements={<RegistrationButtonFooter onBack={onBack} />}
        employeeId={employeeId}
      />
    </>
  )
}

export function ConfirmAccountEmail({ email, onBack, onComplete, employeeId }: ConfirmAccountEmailProps): JSX.Element {
  const [accountNotFound, setAccountNotFound] = useState(false)
  const [enteredEmail, setEnteredEmail] = useState("")

  if (accountNotFound) {
    return <AccountNotFound onBack={() => setAccountNotFound(false)} employeeId={employeeId} />
  }

  return (
    <ConfirmEmailInput
      email={email}
      defaultEmail={enteredEmail}
      onBack={onBack}
      onComplete={onComplete}
      onAccountNotFound={(submittedEmail) => {
        setEnteredEmail(submittedEmail)
        setAccountNotFound(true)
      }}
      employeeId={employeeId}
    />
  )
}
